import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Globe,
  Loader2,
  Mail,
  Plug,
  SlidersHorizontal,
  Unplug,
  XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { EmailSettingsUpdateDto } from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import {
  EMAIL_SURVEY_QUESTIONS,
  type EmailSurveyQuestion,
} from '@/Domain/api/Email/dto/EmailSurveyQuestion.dto';
import { EmailConfigurationDialog } from '@/Domain/components/Integration/Email/EmailConfigurationDialog';
import { EmailDomainDialog } from '@/Domain/components/Integration/Email/EmailDomainDialog';
import {
  EMAIL_INTEGRATION_NAME,
  EMAIL_NS,
  EMAIL_FORWARDING_POLL_INTERVAL_MS,
  EMAIL_QUERY_KEY,
  EMAIL_REPLY_DELAY_MINUTES,
} from '@/Domain/components/Integration/Email/email.const';
import type {
  EmailIdentityForm,
  EmailIntegrationParams,
  EmailIntegrationResponse,
  VerifyDomainResponse,
} from '@/Domain/components/Integration/Email/email.types';
import { replyDelayLabel } from '@/Domain/components/Integration/Email/replyDelayLabel';
import { IntegrationEnabledBadge } from '@/Domain/components/Integration/IntegrationEnabledBadge';
import { IntegrationStatusBadge } from '@/Domain/components/Integration/IntegrationStatusBadge';
import { IntegrationConnectionCard } from '@/Domain/components/Integration/components/IntegrationConnectionCard';
import { IntegrationIntroduction } from '@/Domain/components/Integration/components/IntegrationIntroduction';
import { IntegrationSection } from '@/Domain/components/Integration/components/IntegrationSection';
import { IntegrationToggleField } from '@/Domain/components/Integration/components/IntegrationToggleField';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Domain/components/Shared/Select/Select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import CloseIcon from '@/Domain/components/icons/Close.icon';
import { PostHogEventEnum, type EmailDomainVerifyOutcome } from '@/Domain/enum/PostHogEvent.enum';
import { useAutoSubmit } from '@/Domain/hooks/App/useAutoSave.hook';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useIntegrationConnectFunnel } from '@/Domain/hooks/Integration/useIntegrationConnectFunnel.hook';

interface EmailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type SettingsField = keyof EmailSettingsUpdateDto & keyof EmailIntegrationParams;

const SETTINGS_FIELD_SET: Record<SettingsField, true> = {
  display_name: true,
  signature: true,
  rating_footer_active: true,
  survey_question: true,
  archive_copy_active: true,
  reply_delay_minutes: true,
};
const SETTINGS_FIELDS = Object.keys(SETTINGS_FIELD_SET) as SettingsField[];

const withFields = (
  base: EmailIntegrationParams,
  source: EmailIntegrationParams,
  fields: SettingsField[],
): EmailIntegrationParams =>
  fields.reduce<EmailIntegrationParams>((acc, field) => ({ ...acc, [field]: source[field] }), base);

export const EmailSheet: React.FC<EmailSheetProps> = ({ open, onOpenChange }) => {
  const { t } = useTranslation(['integration', 'integrations']);
  const queryClient = useQueryClient();
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [domainDialogOpen, setDomainDialogOpen] = useState(false);

  const [replyAddress, setReplyAddress] = useState('');
  // Sender name and signature autosave; the reply address does not, because
  // saving it provisions or releases a SendGrid domain.
  const identityForm = useForm<EmailIdentityForm>({
    defaultValues: { display_name: '', signature: '' },
  });

  const integrationQuery = useQuery<EmailIntegrationResponse>({
    queryKey: EMAIL_QUERY_KEY,
    enabled: open,
    // Poll while open until forwarding is observed, so the "forwarding active"
    // indicator flips on its own once the first forwarded email lands (the shared
    // query key otherwise inherits staleTime: Infinity and never refetches).
    refetchInterval: (query) =>
      open && !query.state.data?.params?.forwarding_active
        ? EMAIL_FORWARDING_POLL_INTERVAL_MS
        : false,
  });
  const data = integrationQuery.data?.params;
  const isConnected = Boolean(data?.inbound_address);
  const hasSupportInbox = Boolean(data?.reply_from_address?.trim());

  const { capture } = usePostHogAnalytics();
  const { trackConnectStarted, trackConnectFailed } = useIntegrationConnectFunnel(
    EMAIL_INTEGRATION_NAME,
    isConnected,
  );

  // `null` until the first loaded response, so reopening an already-forwarding integration seeds instead of reporting.
  const forwardingSeen = useRef<boolean | null>(null);
  const forwardingActive = Boolean(data?.forwarding_active);
  useEffect(() => {
    if (!open) {
      forwardingSeen.current = null;
      return;
    }
    if (!data) return;
    const previous = forwardingSeen.current;
    forwardingSeen.current = forwardingActive;
    if (previous !== false || !forwardingActive) return;
    capture(PostHogEventEnum.EmailIntegrationForwardingDetected, {
      has_support_inbox: hasSupportInbox,
      bot_active: Boolean(data.email_bot_active),
    });
  }, [open, data, forwardingActive, hasSupportInbox, capture]);

  const captureAddressCopied = useCallback(
    () =>
      capture(PostHogEventEnum.EmailIntegrationAddressCopied, {
        forwarding_active: forwardingActive,
      }),
    [capture, forwardingActive],
  );

  const captureVerifyOutcome = useCallback(
    (outcome: EmailDomainVerifyOutcome) =>
      capture(PostHogEventEnum.EmailIntegrationDomainVerifyAttempted, { outcome }),
    [capture],
  );

  // Seed editable fields once per connected integration; re-seeding on every refetch would clobber an in-progress edit in another field.
  const seededAddress = useRef<string | null>(null);
  useEffect(() => {
    const address = data?.inbound_address ?? null;
    if (!data || seededAddress.current === address) return;
    seededAddress.current = address;
    setReplyAddress(data.reply_from_address ?? '');
    identityForm.reset({
      display_name: data.display_name ?? '',
      signature: data.signature ?? '',
    });
  }, [data, identityForm]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: EMAIL_QUERY_KEY });

  const connectMutation = useMutation<EmailIntegrationResponse>({
    mutationKey: [QueryKey.integration.emailConnect],
    // Connect returns the same payload as the GET, so seeding the cache is
    // enough; refetching on top of it would race the write back to disconnected.
    onSuccess: (response) => {
      if (response?.params) {
        queryClient.setQueryData(EMAIL_QUERY_KEY, response);
        return;
      }
      invalidate();
    },
    onError: () => {
      trackConnectFailed('connect_failed');
      toast.error(t(`${EMAIL_NS}.connect_failed`));
    },
  });

  // display_name rides along for the rolling-deploy window: an old worker ignores
  // the field on /email/settings, and this endpoint is the only one that version
  // persists it through. The new backend treats it as "unchanged" when it matches.
  const replyAddressMutation = useMutation<
    unknown,
    Error,
    { reply_from_address: string; display_name?: string }
  >({
    mutationKey: [QueryKey.integration.emailReplyAddress],
    onSuccess: (_result, variables) => {
      invalidate();
      if (!variables.reply_from_address.trim()) {
        capture(PostHogEventEnum.EmailIntegrationSupportInboxCleared, {
          had_custom_domain: Boolean(data?.domain_authenticated),
        });
        toast.success(t(`${EMAIL_NS}.reply_address_cleared`));
        return;
      }
      capture(PostHogEventEnum.EmailIntegrationSupportInboxSaved, {
        is_first_save: !hasSupportInbox,
        has_display_name: Boolean(data?.display_name?.trim()),
      });
      toast.success(t(`${EMAIL_NS}.saved`));
    },
    onError: () => {
      capture(PostHogEventEnum.EmailIntegrationSupportInboxSaveFailed, {
        reason: 'save_failed',
      });
      toast.error(t(`${EMAIL_NS}.save_failed`));
    },
  });

  const verifyMutation = useMutation<VerifyDomainResponse>({
    mutationKey: [QueryKey.integration.emailVerifyDomain],
    onSuccess: (result) => {
      invalidate();
      captureVerifyOutcome(result.domain_authenticated ? 'authenticated' : 'pending');
      if (result.domain_authenticated) {
        toast.success(t(`${EMAIL_NS}.verify_succeeded`));
      } else {
        toast.error(result.message || t(`${EMAIL_NS}.verify_pending`));
      }
    },
    onError: () => {
      captureVerifyOutcome('failed');
      toast.error(t(`${EMAIL_NS}.verify_failed`));
    },
  });

  // Several settings writes can be in flight at once (the footer toggle and the
  // identity autosave do not wait for each other), each returning a full
  // snapshot. Writes to the same field are serialised: the server commits them in
  // submission order, so the last acknowledged value is the durable one, and a
  // newer edit waits in the queue until the in-flight one settles. Responses are
  // reconciled per field: a response speaks for the fields it submitted, and for
  // a field nobody submitted only when no write to it was in flight or
  // acknowledged after that request started, so a snapshot taken before another
  // write committed can never revert it.
  const settingsRequestRef = useRef(0);
  const inFlightFieldsRef = useRef<Partial<Record<SettingsField, number>>>({});
  // Per field, the newest request that had started when its last commit was
  // acknowledged: any request up to that id may carry a snapshot from before it.
  const committedFieldsRef = useRef<Partial<Record<SettingsField, number>>>({});
  const queuedSettingsRef = useRef<EmailSettingsUpdateDto>({});
  const pickFields = (
    source: EmailSettingsUpdateDto,
    fields: SettingsField[],
  ): EmailSettingsUpdateDto =>
    fields.reduce<EmailSettingsUpdateDto>((acc, field) => ({ ...acc, [field]: source[field] }), {});

  // Autosaved, so no success toast: one per 800ms typing pause would be noise.
  // The response is the same payload as the GET, so seeding beats a refetch.
  const settingsMutation = useMutation<
    EmailIntegrationResponse,
    Error,
    EmailSettingsUpdateDto,
    { requestId: number }
  >({
    mutationKey: [QueryKey.integration.emailSettings],
    onMutate: async (variables) => {
      settingsRequestRef.current += 1;
      (Object.keys(variables) as SettingsField[]).forEach((field) => {
        inFlightFieldsRef.current[field] = settingsRequestRef.current;
      });
      // A refetch already in flight read the server before this write commits.
      await queryClient.cancelQueries({ queryKey: EMAIL_QUERY_KEY });
      return { requestId: settingsRequestRef.current };
    },
    onSuccess: async (response, variables, context) => {
      if (!response?.params) {
        invalidate();
        return;
      }
      const requestId = context?.requestId ?? 0;
      const isLatestRequest = requestId === settingsRequestRef.current;
      const submittedFields = Object.keys(variables) as SettingsField[];
      const carriedFields = SETTINGS_FIELDS.filter(
        (field) =>
          !submittedFields.includes(field) &&
          inFlightFieldsRef.current[field] === undefined &&
          requestId > (committedFieldsRef.current[field] ?? 0),
      );
      const appliedFields = [...submittedFields, ...carriedFields];
      // A refetch that started while this write was in flight may resolve with a
      // snapshot from before its commit; cancel it before seeding the cache (the
      // cancel reverts the query to its pre-fetch state, so it must finish first).
      await queryClient.cancelQueries({ queryKey: EMAIL_QUERY_KEY });
      queryClient.setQueryData<EmailIntegrationResponse>(EMAIL_QUERY_KEY, (cached) => {
        if (!cached?.params) {
          return response;
        }
        // Fields no settings write touches are safe from any snapshot; take the newest.
        const base = isLatestRequest
          ? withFields(response.params, cached.params, SETTINGS_FIELDS)
          : cached.params;
        return {
          ...(isLatestRequest ? response : cached),
          params: withFields(base, response.params, appliedFields),
        };
      });
      submittedFields.forEach((field) => {
        committedFieldsRef.current[field] = settingsRequestRef.current;
      });
      // Re-baseline each identity field on what the server acknowledged, but only
      // from a response allowed to speak for that field. The comparison is against
      // what this request submitted, not against the response: the endpoint trims,
      // so a name typed with spaces comes back different and treating that as a
      // concurrent edit would resubmit it every debounce, forever. Only a value that
      // changed while the request was in flight stays dirty.
      (['display_name', 'signature'] as const).forEach((field) => {
        if (!appliedFields.includes(field)) {
          return;
        }
        const submitted = variables[field];
        const saved = response.params[field] ?? '';
        const current = identityForm.getValues(field);
        const editedInFlight = submitted === undefined ? current !== saved : current !== submitted;
        identityForm.resetField(field, { defaultValue: saved });
        if (editedInFlight) {
          identityForm.setValue(field, current, { shouldDirty: true });
        }
      });
    },
    onError: () => {
      // Nothing was committed and nothing was applied optimistically; the form
      // keeps the unsaved edit dirty, so the next autosave resubmits it.
      toast.error(t(`${EMAIL_NS}.save_failed`));
    },
    onSettled: (_data, _error, variables, context) => {
      (Object.keys(variables) as SettingsField[]).forEach((field) => {
        if (inFlightFieldsRef.current[field] === context?.requestId) {
          delete inFlightFieldsRef.current[field];
        }
      });
      const readyFields = (Object.keys(queuedSettingsRef.current) as SettingsField[]).filter(
        (field) => inFlightFieldsRef.current[field] === undefined,
      );
      if (readyFields.length > 0) {
        const queued = pickFields(queuedSettingsRef.current, readyFields);
        readyFields.forEach((field) => {
          delete queuedSettingsRef.current[field];
        });
        settingsMutation.mutate(queued);
      }
    },
  });
  const submitSettings = (variables: EmailSettingsUpdateDto) => {
    const fields = Object.keys(variables) as SettingsField[];
    if (fields.some((field) => inFlightFieldsRef.current[field] !== undefined)) {
      // The newest edit replaces anything queued before it for the same field.
      queuedSettingsRef.current = { ...queuedSettingsRef.current, ...variables };
      return;
    }
    settingsMutation.mutate(variables);
  };

  useAutoSubmit<EmailIdentityForm>(
    identityForm,
    (values) => submitSettings(values as EmailSettingsUpdateDto),
    true,
    true,
  );

  const toggleActiveMutation = useMutation<
    unknown,
    Error,
    { name: typeof EMAIL_INTEGRATION_NAME; params: { email_bot_active: boolean } }
  >({
    mutationKey: [QueryKey.integration.patch],
    onSuccess: (_d, variables) => {
      invalidate();
      capture(PostHogEventEnum.EmailIntegrationActiveToggled, {
        enabled: variables.params.email_bot_active,
        forwarding_active: forwardingActive,
      });
      const name = t(`${EMAIL_NS}.title`);
      toast.success(
        variables.params.email_bot_active
          ? t('integrations:toast.enabled', { name })
          : t('integrations:toast.disabled', { name }),
      );
    },
    onError: (_e, variables) => {
      capture(PostHogEventEnum.EmailIntegrationActiveToggleFailed, {
        enabled: variables.params.email_bot_active,
        reason: 'save_failed',
      });
      toast.error(t(`${EMAIL_NS}.save_failed`));
    },
  });

  const removeMutation = useMutation({
    mutationKey: [QueryKey.integration.emailRemove],
    onSuccess: () => {
      invalidate();
      capture(PostHogEventEnum.EmailIntegrationRemoved, {
        was_active: Boolean(data?.email_bot_active),
        forwarding_active: forwardingActive,
      });
      setConfirmRemoveOpen(false);
      setConfigDialogOpen(false);
      setDomainDialogOpen(false);
    },
    onError: () => toast.error(t(`${EMAIL_NS}.remove_failed`)),
  });

  const ratingFooterActive = data?.rating_footer_active ?? false;
  const ratingFooterSaving =
    settingsMutation.isPending && settingsMutation.variables?.rating_footer_active !== undefined;
  const surveyQuestion: EmailSurveyQuestion = data?.survey_question ?? 'need_more_help';
  const surveyQuestionSaving =
    useIsMutating({
      mutationKey: [QueryKey.integration.emailSettings],
      predicate: (mutation) =>
        (mutation.state.variables as EmailSettingsUpdateDto | undefined)?.survey_question !==
        undefined,
    }) > 0;
  const archiveCopyActive = data?.archive_copy_active ?? false;
  // Every in-flight write of this field, not just the newest observer's: an
  // identity autosave settling first would otherwise unlock the switch mid-save.
  const archiveCopySaving =
    useIsMutating({
      mutationKey: [QueryKey.integration.emailSettings],
      predicate: (mutation) =>
        (mutation.state.variables as EmailSettingsUpdateDto | undefined)?.archive_copy_active !==
        undefined,
    }) > 0;
  const replyDelayMinutes = data?.reply_delay_minutes ?? 0;
  const replyDelaySaving =
    settingsMutation.isPending && settingsMutation.variables?.reply_delay_minutes !== undefined;
  // Every in-flight settings write, not just this observer's latest: a delay save
  // must not be overwritten by a choice made while an identity autosave settled first.
  const settingsWriteInFlight =
    useIsMutating({ mutationKey: [QueryKey.integration.emailSettings] }) > 0;
  const prerequisitesRaw = t(`${EMAIL_NS}.prerequisites`, { returnObjects: true });
  const prerequisites = Array.isArray(prerequisitesRaw) ? (prerequisitesRaw as string[]) : [];
  const supportInboxSummary =
    data?.reply_from_address || t(`${EMAIL_NS}.support_inbox_not_configured`);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="max-w-2xl w-full h-full pb-0"
        tabIndex={undefined}
        data-testid="email-sheet"
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          onOpenChange(false);
        }}
      >
        <SheetHeader className="px-0.5 items-center flex-row">
          <div className="flex min-w-0 items-center gap-3">
            <Mail className="size-8 shrink-0" />
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <SheetTitle data-testid="email-sheet-title">{t(`${EMAIL_NS}.title`)}</SheetTitle>
              <IntegrationStatusBadge connected={isConnected} />
              <IntegrationEnabledBadge enabled={Boolean(data?.email_bot_active)} />
              {Boolean(data?.email_bot_active) && !forwardingActive && (
                <Badge
                  variant="warning"
                  size="sm"
                  className="px-2"
                  data-testid="email-not-receiving-badge"
                >
                  {t(`${EMAIL_NS}.not_receiving_badge`)}
                </Badge>
              )}
            </div>
          </div>
          <IconButton
            onClick={() => onOpenChange(false)}
            variant="secondary"
            data-testid="email-close-button"
            className="shrink-0"
          >
            <CloseIcon />
          </IconButton>
        </SheetHeader>

        <div className="overflow-y-auto px-0.5 pb-6 space-y-6 flex-1">
          {integrationQuery.isLoading ? (
            <div className="flex items-center justify-center py-12" data-testid="email-loading">
              <Loader2 className="size-6 animate-spin text-foreground-subtle" />
            </div>
          ) : (
            <>
              <IntegrationIntroduction
                description={t(`${EMAIL_NS}.overview`)}
                prerequisites={prerequisites}
                docsHref="https://docs.quickchat.ai/channels/email"
              />

              <IntegrationSection
                title={t('integrations:section.connect')}
                data-testid="email-connect-section"
              >
                {!isConnected ? (
                  <Button
                    type="button"
                    variant="dark"
                    size="sm"
                    className="w-full"
                    onClick={() => {
                      trackConnectStarted();
                      connectMutation.mutate(undefined);
                    }}
                    disabled={connectMutation.isPending}
                    data-testid="email-connect-button"
                  >
                    {connectMutation.isPending ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Plug className="size-4" />
                    )}
                    {t('integrations:connect', { name: t(`${EMAIL_NS}.title`) })}
                  </Button>
                ) : (
                  <IntegrationConnectionCard
                    data-testid="email-connection-card"
                    details={[
                      {
                        label: t(`${EMAIL_NS}.forwarding_label`),
                        value: data?.forwarding_active ? (
                          <span
                            className="flex items-center gap-1 text-success"
                            data-testid="email-card-forwarding"
                          >
                            <CheckCircle2 className="size-3.5" />
                            {t(`${EMAIL_NS}.forwarding_status_active`)}
                          </span>
                        ) : (
                          <span
                            className="flex items-center gap-1 text-muted-foreground"
                            data-testid="email-card-forwarding"
                          >
                            <Clock className="size-3.5" />
                            {t(`${EMAIL_NS}.forwarding_status_pending`)}
                          </span>
                        ),
                      },
                      {
                        label: t(`${EMAIL_NS}.reply_address_label`),
                        value: supportInboxSummary,
                        'data-testid': 'email-card-support-inbox',
                      },
                    ]}
                    actions={
                      // Three actions do not fit one row on a narrow viewport,
                      // and button labels never wrap.
                      <div className="flex flex-1 flex-wrap gap-2">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          className="flex-1"
                          onClick={() => setConfigDialogOpen(true)}
                          data-testid="email-settings-open-button"
                        >
                          <SlidersHorizontal className="size-4" />
                          {t(`${EMAIL_NS}.configure`)}
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          className="flex-1"
                          onClick={() => setDomainDialogOpen(true)}
                          data-testid="email-domain-open-button"
                        >
                          <Globe className="size-4" />
                          {t(`${EMAIL_NS}.domain_configure`)}
                        </Button>
                        <Button
                          type="button"
                          variant="danger"
                          size="sm"
                          className="flex-1"
                          onClick={() => setConfirmRemoveOpen(true)}
                          data-testid="email-remove-button"
                        >
                          <Unplug className="size-4" />
                          {t('integrations:disconnect')}
                        </Button>
                      </div>
                    }
                  />
                )}
              </IntegrationSection>

              {isConnected ? (
                <IntegrationSection
                  title={t('integrations:section.settings')}
                  data-testid="email-enable-section"
                >
                  <IntegrationToggleField
                    id="email-active-toggle"
                    label={t('integrations:enable', { name: t(`${EMAIL_NS}.title`) })}
                    checked={Boolean(data?.email_bot_active)}
                    onCheckedChange={(checked) =>
                      toggleActiveMutation.mutate({
                        name: EMAIL_INTEGRATION_NAME,
                        params: { email_bot_active: checked },
                      })
                    }
                    disabled={toggleActiveMutation.isPending}
                    data-testid="email-active-toggle"
                  />

                  <div className="space-y-2" data-testid="email-rating-footer-section">
                    <IntegrationToggleField
                      id="email-rating-footer-toggle"
                      label={t(`${EMAIL_NS}.rating_footer.label`)}
                      description={t(`${EMAIL_NS}.rating_footer.description`)}
                      tooltip={t(`${EMAIL_NS}.rating_footer.tooltip`)}
                      checked={ratingFooterActive}
                      onCheckedChange={(checked) =>
                        submitSettings({
                          rating_footer_active: checked,
                          // The live form value, not the cached one: a signature edit
                          // still inside the autosave debounce would otherwise be
                          // overwritten by whatever the server last returned.
                          signature: identityForm.getValues('signature') ?? '',
                        })
                      }
                      disabled={settingsMutation.isPending}
                      loading={ratingFooterSaving}
                      data-testid="email-rating-footer-switch"
                    />
                    {ratingFooterActive ? (
                      <div
                        className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
                        data-testid="email-survey-question-section"
                      >
                        <div className="space-y-0.5">
                          <p
                            className="text-sm font-medium text-foreground"
                            id="email-survey-question-label"
                          >
                            {t(`${EMAIL_NS}.rating_footer.question.label`)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {t(`${EMAIL_NS}.rating_footer.question.description`)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {surveyQuestionSaving ? (
                            <Loader2
                              className="size-3.5 shrink-0 animate-spin text-muted-foreground"
                              data-testid="email-survey-question-saving"
                            />
                          ) : null}
                          <Select
                            value={surveyQuestion}
                            onValueChange={(value) =>
                              submitSettings({ survey_question: value as EmailSurveyQuestion })
                            }
                            disabled={settingsWriteInFlight}
                          >
                            <SelectTrigger
                              className="w-full sm:w-64"
                              aria-labelledby="email-survey-question-label"
                              data-testid="email-survey-question-select"
                            >
                              <SelectValue>
                                {t(`${EMAIL_NS}.rating_footer.question.options.${surveyQuestion}`)}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent className="w-[var(--radix-select-trigger-width)]">
                              {EMAIL_SURVEY_QUESTIONS.map((question) => (
                                <SelectItem
                                  key={question}
                                  value={question}
                                  data-testid={`email-survey-question-option-${question}`}
                                >
                                  {t(`${EMAIL_NS}.rating_footer.question.options.${question}`)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <div className="space-y-2" data-testid="email-archive-copy-section">
                    <IntegrationToggleField
                      id="email-archive-copy-toggle"
                      label={t(`${EMAIL_NS}.archive_copy.label`)}
                      description={t(`${EMAIL_NS}.archive_copy.description`)}
                      tooltip={t(`${EMAIL_NS}.archive_copy.tooltip`)}
                      checked={archiveCopyActive}
                      onCheckedChange={(checked) =>
                        submitSettings({ archive_copy_active: checked })
                      }
                      disabled={settingsWriteInFlight || !hasSupportInbox}
                      loading={archiveCopySaving}
                      data-testid="email-archive-copy-switch"
                    />
                    {!hasSupportInbox ? (
                      <p
                        className="flex items-start gap-1.5 text-xs text-muted-foreground"
                        data-testid="email-archive-copy-requires-address"
                      >
                        <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-warning" />
                        {t(`${EMAIL_NS}.archive_copy.requires_address`)}
                      </p>
                    ) : null}
                  </div>

                  <div className="space-y-2" data-testid="email-reply-delay-section">
                    <div className="flex items-center justify-between gap-4">
                      <div className="space-y-0.5">
                        <p
                          className="text-sm font-medium text-foreground"
                          id="email-reply-delay-label"
                        >
                          {t(`${EMAIL_NS}.reply_delay.label`)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t(`${EMAIL_NS}.reply_delay.description`)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {replyDelaySaving ? (
                          <Loader2 className="size-3.5 shrink-0 animate-spin text-muted-foreground" />
                        ) : null}
                        <Select
                          value={String(replyDelayMinutes)}
                          onValueChange={(value) =>
                            submitSettings({ reply_delay_minutes: Number(value) })
                          }
                          disabled={settingsWriteInFlight}
                        >
                          <SelectTrigger
                            className="w-40"
                            aria-labelledby="email-reply-delay-label"
                            data-testid="email-reply-delay-select"
                          >
                            <SelectValue>{replyDelayLabel(t, replyDelayMinutes)}</SelectValue>
                          </SelectTrigger>
                          <SelectContent className="w-[var(--radix-select-trigger-width)]">
                            {EMAIL_REPLY_DELAY_MINUTES.map((minutes) => (
                              <SelectItem
                                key={minutes}
                                value={String(minutes)}
                                data-testid={`email-reply-delay-option-${minutes}`}
                              >
                                {replyDelayLabel(t, minutes)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </IntegrationSection>
              ) : null}
            </>
          )}
        </div>

        <EmailConfigurationDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          params={data}
          control={identityForm.control}
          onAddressCopied={captureAddressCopied}
          onOpenDomain={() => {
            setConfigDialogOpen(false);
            setDomainDialogOpen(true);
          }}
        />

        <EmailDomainDialog
          open={domainDialogOpen}
          onOpenChange={setDomainDialogOpen}
          params={data}
          replyAddress={replyAddress}
          onReplyAddressChange={setReplyAddress}
          onSaveReplyAddress={() =>
            replyAddressMutation.mutate({
              reply_from_address: replyAddress,
              display_name: identityForm.getValues('display_name') ?? '',
            })
          }
          onClearReplyAddress={() => {
            setReplyAddress('');
            replyAddressMutation.mutate({
              reply_from_address: '',
              display_name: identityForm.getValues('display_name') ?? '',
            });
          }}
          isSavingReplyAddress={replyAddressMutation.isPending}
          onVerify={() => verifyMutation.mutate()}
          isVerifying={verifyMutation.isPending}
          onAddressCopied={captureAddressCopied}
        />

        <AlertDialogPrimitive.Root open={confirmRemoveOpen} onOpenChange={setConfirmRemoveOpen}>
          <AlertDialogPrimitive.Overlay className="!mt-0 absolute inset-0 bg-black/30 backdrop-blur-sm z-20" />
          <AlertDialogPrimitive.Content className="absolute inset-0 flex items-center justify-center p-4 z-30 focus:outline-none">
            <div
              className="space-y-4 bg-card rounded-lg p-6 max-w-md w-full shadow-lg"
              data-testid="email-remove-dialog"
            >
              <div className="flex items-center gap-3">
                <AlertTriangle className="size-6 text-destructive" />
                <AlertDialogPrimitive.Title className="text-lg font-semibold text-foreground">
                  {t(`${EMAIL_NS}.remove_confirm.title`)}
                </AlertDialogPrimitive.Title>
              </div>
              <AlertDialogPrimitive.Description className="text-sm text-muted-foreground">
                {t(`${EMAIL_NS}.remove_confirm.description`)}
              </AlertDialogPrimitive.Description>
              <div className="flex justify-end gap-2">
                <AlertDialogPrimitive.Cancel asChild>
                  <Button variant="secondary" size="md" data-testid="email-remove-cancel-button">
                    {t(`${EMAIL_NS}.remove_confirm.cancel`)}
                  </Button>
                </AlertDialogPrimitive.Cancel>
                <Button
                  variant="danger"
                  size="md"
                  onClick={() => removeMutation.mutate(undefined)}
                  disabled={removeMutation.isPending}
                  data-testid="email-remove-confirm-button"
                >
                  {removeMutation.isPending ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <XCircle className="size-4" />
                  )}
                  {t(`${EMAIL_NS}.remove_confirm.confirm`)}
                </Button>
              </div>
            </div>
          </AlertDialogPrimitive.Content>
        </AlertDialogPrimitive.Root>
      </SheetContent>
    </Sheet>
  );
};
